import smoothscroll from 'smoothscroll-polyfill';
import { hideElement, showElement } from '../../../shared/utilities/show-hide';
import restrictBodyScroll from '../../../shared/utilities/restrict-body-scroll';
import pubsub from '../../../shared/utilities/pubsub';
import {
	addTrapFocusListener,
	removeTrapFocusListener
} from '../../../shared/utilities/trap-focus';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';
import BaseBehavior from '../../../framework/base/base';

// kick off the polyfill as it is required for element scrollTo method
smoothscroll.polyfill();

const toggleMarker = 'toggled';
const triggerMarker = 'disclaimer--triggered';

export default class Disclaimer extends BaseBehavior {
	constructor(element) {
		super(element);
		// sections
		this.initDomCache();
		this.modifier = {
			info: 'disclaimer--info',
			manage: 'disclaimer--consent',
			details: 'disclaimer--details',
			language: 'disclaimer--countries'
		};
		this.deferCookieKey = 'skip-cookie-bot';

		this.navStack = [this.modifier.info]; // initial nav stack item
		this.popupRendered = false;

		let timeoutDelay = 100;
		const self = this;
		let timer = setTimeout(function checkDOMReadyState() {
			if (document.readyState === 'complete') {
				self.addEventListeners();
				self.init();
				clearTimeout(timer);
			} else {
				timeoutDelay += 100;
				timer = setTimeout(checkDOMReadyState, timeoutDelay);
			}
		}, timeoutDelay);

		this.cookieDisclaimer = document.querySelector('.v2-cookie-disclaimer');
		this.showLess();
	}

	addEventListeners() {
		window.addEventListener(
			'CookiebotOnDialogInit',
			() => {
				// ensures Cookiebot laoded
				this.init();
			},
			false
		);

		this.addEventListener(this.dom.manageBtn, 'click.manage', e => {
			e.preventDefault();
			this.addIntroToCookiesGroup();
			this.showConsentGroupSection();
		});
		this.addEventListener(this.dom.necessaryOnlyBtn, 'click', e => {
			e.preventDefault();
			/* istanbul ignore else */
			if (window.Cookiebot) {
				window.Cookiebot.submitCustomConsent(false, false, false);
			}
			this.hidePopup();
		});

		this.addEventListener(this.dom.backBtn, 'click.back', e => {
			e.preventDefault();
			const prevState = this.navStack.splice(-1, 1)[0]; // remove state from stack
			this.showSectionsForState(this.navStack[this.navStack.length - 1], prevState);
		});

		this.addEventListener(this.dom.acceptBtn, 'click.accept', this.onAcceptAll.bind(this));
		this.addEventListener(this.dom.consentAcceptBtn, 'click.accept', this.onAcceptAll.bind(this));

		this.addEventListener(this.dom.updateBtn, 'click.update', this.onUpdate.bind(this));
		/* istanbul ignore else */
		if (this.element.hasAttribute('data-has-culture-selector')) {
			this.addEventListener(this.dom.cultureSelector, 'click.culture', e => {
				e.preventDefault();
				this.showCountriesSection();
			});
		}

		// mark checkboxes toggle to select the correct toggled checkbox
		this.element.querySelector('.disclaimer__consent--cookies-group').addEventListener(
			'click',
			e => {
				const { target } = e;
				/* istanbul ignore else */
				if (target.type === 'checkbox') {
					e.target.classList.add(toggleMarker);
				} else if (target.dataset.showMoreLink) {
					this.onShowMore(target.dataset.showMoreLink);
				}
			},
			false
		);

		// subscribe to trigger
		pubsub.subscribe('disclaimer/open', () => {
			this.dom.wrapper.classList.add(triggerMarker);
			window.Cookiebot.process(); // initialize cookiebot.dialog object and raise CookiebotDialogInit event
		});

		// subscribe to trigger
		pubsub.subscribe('disclaimer/open-with-dependency', () => {
			window.Cookiebot.process(); // initialize cookiebot.dialog object and raise CookiebotDialogInit event
		});

		this.addEventListener(this.dom.deferCookieBot, 'click', () => {
			sessionStorage.setItem(this.deferCookieKey, 'true');
			this.hidePopup();
		});
	}

	init() {
		if (window.navigator.isIOSTrackingPermissionAllowed !== undefined) {
			window.navigator.isIOSTrackingPermissionAllowed
				? window.Cookiebot.submitCustomConsent(true, true, true)
				: window.Cookiebot.submitCustomConsent(false, false, false);
			// Asked by analytics team to add this event here as sometimes the inital page load is not tracked
			if (window.adobeDataLayer) {
				window.adobeDataLayer.push({ event: 'web.webpagedetails.pageViews' });
			}
		}
		/* istanbul ignore else */
		if (this.element.classList.contains('v2-hidden')) {
			if (this.dom.wrapper.classList.contains(triggerMarker)) {
				this.addIntroToCookiesGroup();
				this.showPopup();
				this.showConsentGroupSection();
				this.dom.wrapper.classList.remove(triggerMarker);
			} else if (
				!window.Cookiebot.consented &&
				!window.Cookiebot.declined &&
				!this.element.dataset.hideonpageload
			) {
				this.showPopup();
			}
		}
	}

	showPopup() {
		if (!this.popupRendered && !sessionStorage.getItem(this.deferCookieKey)) {
			this.checkConsents();
			showElement(this.element);
			this.popupRendered = true;
			restrictBodyScroll(true);
			this.handleTrapFocusForLanding();
		}
	}

	hidePopup() {
		hideElement(this.element);
		restrictBodyScroll(false);
		removeTrapFocusListener(this);
		pubsub.publish('institution-disclaimer/open', true);
	}

	changeCookiesCategoryValues(type, intro, header) {
		const regex = /[\s\p{Alpha}\p{M}\p{Pc}\p{Join_C}-]+/gu;
		this.element.querySelector(`.cookies-category-intro--${type}`).innerHTML = intro;
		let headerTitleString = header.match(regex);
		headerTitleString = headerTitleString ? headerTitleString[0].trim() : '';
		this.element.querySelector(`.cookies-category-header--${type}`).innerHTML = headerTitleString;
	}

	addIntroToCookiesGroup() {
		if (window.Cookiebot.dialog) {
			const {
				cookieIntroTypeAdvertising,
				cookieIntroTypeNecessary,
				cookieIntroTypePreference,
				cookieIntroTypeStatistics,
				cookieHeaderTypeAdvertising,
				cookieHeaderTypeNecessary,
				cookieHeaderTypePreference,
				cookieHeaderTypeStatistics
			} = window.Cookiebot.dialog;

			this.changeCookiesCategoryValues(
				'necessary',
				cookieIntroTypeNecessary,
				cookieHeaderTypeNecessary
			);
			this.changeCookiesCategoryValues(
				'preference',
				cookieIntroTypePreference,
				cookieHeaderTypePreference
			);
			this.changeCookiesCategoryValues(
				'statistics',
				cookieIntroTypeStatistics,
				cookieHeaderTypeStatistics
			);
			this.changeCookiesCategoryValues(
				'marketing',
				cookieIntroTypeAdvertising,
				cookieHeaderTypeAdvertising
			);
		}
	}

	showSectionsForState(nextModifier, prevModifier) {
		this.dom.wrapper.classList.remove(prevModifier);
		this.dom.wrapper.classList.add(nextModifier);

		removeTrapFocusListener(this);
		switch (nextModifier) {
			case this.modifier.info: {
				this.handleTrapFocusForLanding();
				break;
			}
			case this.modifier.manage: {
				this.handleTrapFocusForConsent();
				break;
			}
			case this.modifier.details: {
				this.handleTrapFocusForDetails();
				break;
			}
			default: {
				this.handleTrapFocusForLanding();
			}
		}
		// set scroll to top for content section
		this.dom.wrapper.querySelector('.disclaimer__content').scrollTo(0, 0);
	}

	showCountriesSection() {
		// hide cookies and consent sections, btn container, lang selector
		this.showSectionsForState(this.modifier.language, this.navStack[this.navStack.length - 1]);
		this.navStack.push(this.modifier.language);
	}

	showCookiesSection() {
		this.showSectionsForState(this.modifier.info, this.navStack[this.navStack.length - 1]);
		this.navStack.push(this.modifier.info);
	}

	showConsentGroupSection() {
		this.showSectionsForState(this.modifier.manage, this.navStack[this.navStack.length - 1]);
		this.navStack.push(this.modifier.manage);
	}

	showConsentDetailSection() {
		this.showSectionsForState(this.modifier.details, this.navStack[this.navStack.length - 1]);
		this.navStack.push(this.modifier.details);
	}

	onAcceptAll(e) {
		e.preventDefault();
		// call accept all cookies method
		/* istanbul ignore else */
		if (window.Cookiebot) {
			window.Cookiebot.submitCustomConsent(true, true, true);
			this.hidePopup();
			this.showLess();
		}
	}

	showLess() {
		/* istanbul ignore else */
		if (this.cookieDisclaimer && this.element.classList.contains('v2-hidden')) {
			this.cookieDisclaimer.classList.add('v2-cookie-disclaimer--accepted');
			!this.cookieDisclaimer.dataset.extendedMobile &&
				this.cookieDisclaimer.classList.add('v2-cookie-disclaimer--short-show');
			this.cookieDisclaimer.classList.remove('v2-cookie-disclaimer--extended-show');
			this.publish('disclaimer/adjust/height');
		}
	}

	getInputValue(category) {
		const { preferences, statistics, marketing } = window.Cookiebot.consent;
		const inp = this.element.querySelector(`.disclaimer__${category}.${toggleMarker}`);
		if (inp) return inp.checked;

		switch (category) {
			case 'preference':
				return preferences;
			case 'statistics':
				return statistics;
			case 'marketing':
				return marketing;
			default:
				return null;
		}
	}

	setInputValue(category, value) {
		const [mobileInp, desktopInp] = Array.from(
			this.element.querySelectorAll(`.disclaimer__${category}`)
		);
		desktopInp.checked = value;
		mobileInp.checked = value;
	}

	onUpdate(e) {
		e.preventDefault();
		// call accept all cookies method
		/* istanbul ignore else */
		if (window.Cookiebot) {
			window.Cookiebot.submitCustomConsent(
				this.getInputValue('preference'),
				this.getInputValue('statistics'),
				this.getInputValue('marketing')
			);
			this.hidePopup();
			this.showLess();
		}
	}

	onShowMore(showMoreLinkValue) {
		let details;
		let type;
		/* istanbul ignore else */
		if (window.Cookiebot.dialog) {
			const {
				cookieTableNecessary = [],
				cookieTablePreference = [],
				cookieTableStatistics = [],
				cookieTableAdvertising = [],
				cookieHeaderTypeAdvertising,
				cookieHeaderTypeNecessary,
				cookieHeaderTypePreference,
				cookieHeaderTypeStatistics
			} = window.Cookiebot.dialog;

			switch (showMoreLinkValue) {
				case 'necessary':
					details = cookieTableNecessary;
					type = cookieHeaderTypeNecessary;
					break;
				case 'preferences':
					details = cookieTablePreference;
					type = cookieHeaderTypePreference;
					break;
				case 'statistics':
					details = cookieTableStatistics;
					type = cookieHeaderTypeStatistics;
					break;
				case 'marketing':
					details = cookieTableAdvertising;
					type = cookieHeaderTypeAdvertising;
					break;
				default:
					type = 'Unknown';
					details = [];
			}

			if (details.length) this.appendDetails(details, type.split(' ')[0]);
		}
	}

	appendDetails(details, type) {
		let htmlString = '';
		const {
			cookieTableHeaderType,
			cookieTableHeaderName,
			cookieTableHeaderExpiry,
			cookieTableHeaderProvider
		} = window.Cookiebot.dialog;
		details.forEach(cookieDetail => {
			htmlString += `<div class="cookie-detail">
			<div class="v2-flexbox justify-between">
				<label class="v2-text--bold">${cookieTableHeaderName}</label>
				<span class="v2-text-align--right">${cookieDetail[0]}</span>
			</div>
			<div class="v2-flexbox justify-between">
				<label class="v2-text--bold">${cookieTableHeaderProvider}</label>
				<span class="v2-text-align--right">${cookieDetail[1]}</span>
			</div>
			<div class="v2-flexbox justify-between">
				<label class="v2-text--bold">${cookieTableHeaderExpiry}</label>
				<span class="v2-text-align--right">${cookieDetail[3]}</span>
			</div>
			<div class="v2-flexbox justify-between">
				<label class="v2-text--bold">${cookieTableHeaderType}</label>
				<span class="v2-text-align--right">${cookieDetail[4]}</span>
			</div>
			<div class="v2-pat8">${cookieDetail[2]}</div>
			</div>`;
		});
		this.element.querySelector('.header-title--text-details').innerHTML = type + ' Cookies';
		this.dom.cookiesDetails.innerHTML = htmlString;
		this.showConsentDetailSection();
	}

	checkConsents() {
		const { preferences, statistics, marketing } = window.Cookiebot.consent;
		// show checked cookies
		this.setInputValue('preference', preferences);
		this.setInputValue('statistics', statistics);
		this.setInputValue('marketing', marketing);
	}

	handleTrapFocusForLanding() {
		const selector = `#cookie-popup .${this.modifier.info}`;
		const focusableElements = [
			'.disclaimer__cookies__info [href]',
			'.disclaimer__btn-accept.hide-in-consent',
			'.disclaimer__btn-necessaryonly',
			'.disclaimer__btn-manage'
		].join(', ');
		addTrapFocusListener(this, { selector, focusableElements });
	}

	handleTrapFocusForConsent() {
		const deviceShowHideSelector = screenSmallerThan('sm') ? '.v2-hide-sm' : '.v2-show-sm';
		const selector = `#cookie-popup .${this.modifier.manage}`;
		const focusableElements = [
			'.backBtn',
			`.disclaimer__btn-accept.show-in-consent`,
			`.disclaimer__btn-update`,
			`.disclaimer__consent--cookies-group ${deviceShowHideSelector} input[type="checkbox"]`,
			`.disclaimer__consent--cookies-group button.v2-btn--link`
		].join(', ');
		addTrapFocusListener(this, { selector, focusableElements });
	}

	handleTrapFocusForDetails() {
		const selector = `#cookie-popup .${this.modifier.details}`;
		const focusableElements = ['.backBtn'].join(', ');
		addTrapFocusListener(this, { selector, focusableElements });
	}

	removeEventListeners() {
		removeTrapFocusListener(this);
	}

	cleanup() {
		this.removeEventListeners();
	}
}
